import { all, call, delay, put, takeLatest, select } from "redux-saga/effects";
import { normalize, schema } from "normalizr";
import { toast } from "react-toastify";
import queryString from "query-string";
import { getErrorMessage } from "utils/sagasErrorHandler";
import { getPercent } from "utils/helpers/dataFormat";
import { pidProcessRequest } from "modules/Scheduler/services/InitialScrapeTasks/api";
import { sharedHistory } from "routes/sharedHistory";
import { ROUTES_MAP } from "routes/constants";
import {
    createTaskRequest,
    deleteTaskRequest,
    getMetricDataCountRequest,
    getMetricDataRequest,
    getMetricTaskByCodeRequest,
    getMetricTasksRequest,
    getTaskByCodeRequest,
    updateAllTaskDataRequest,
    updateMetricTaskRequest,
    updateTaskRequest
} from "modules/Metric/services/dashboard/api";
import {
    getMetricDataByTask,
    getMetricDataByTaskError,
    getMetricDataByTaskSuccess,
    getMetricTaskByCode,
    getMetricTasks,
    getMetricTasksSuccess,
    setPercentProgress,
    mergeUpdateMetricTaskDialog,
    updateMetric,
    setUpdateMetricTaskDialog,
    getTaskByCode,
    getTaskByCodeSuccess,
    getTaskByCodeError,
    createMetricTask,
    updateMetricTask,
    deleteMetricTask,
    updateMetricTaskInfo, formUpdateMetricTask, refreshMetricTaskInfo
} from "modules/Metric/services/dashboard/actions";
import { metricUpdatingDialogSelector } from "modules/Metric/services/dashboard/selectors";

const metricTasksSchema = new schema.Entity("metric_tasks", {},{ idAttribute: "code" });
const metricTasksListSchema = new schema.Array(metricTasksSchema);

//Workers
function* handleRefreshTask () {
    const { search, pathname } = window.location;
    const { dimension1, dimension2, dimension3, dimension4 } = queryString.parse(search);
    const params = { dimension1, dimension2, dimension3, dimension4 };

    const code = pathname.split("/")[3];

    yield put(getMetricDataByTask({
        code,
        params
    }));
}

function* handleGetMetric (data) {
    try {
        const filterParams = data?.payload && { metric_group_code: data.payload };

        const { count, metric_tasks } = yield call(getMetricTasksRequest, filterParams);
        const { result, entities } = normalize(metric_tasks, metricTasksListSchema);

        yield put(getMetricTasksSuccess({
            codes: result,
            metricTasks: entities.metric_tasks,
            count
        }));
    } catch (error) {
        getErrorMessage(error);
    }
}

function* getMetricData(filterParams, data = [], count) {
    try {
        const LIMIT = 1000;
        filterParams = { ...filterParams, limit: LIMIT };

        const { metric_data } = yield call(getMetricDataRequest, filterParams);
        const newData = [ ...data, ...metric_data ];

        const percent = getPercent(count, newData.length);
        yield put(setPercentProgress(percent));

        if(newData.length !== Number(count)) {
            const newFilterParams = { ...filterParams };
            newFilterParams.offset = Number(newFilterParams.offset) + Number(LIMIT);

            return yield getMetricData(newFilterParams, newData, count);
        }

        return newData;
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleGetMetricDataByTask ({ payload }) {
    try {
        const { code, params } = payload;
        yield put(updateMetric({ code }));
        yield handleGetMetricTaskByCode({ payload: code });
        const filterParams = { metric_code: code, offset: 0, ...params };

        const { count } = yield call(getMetricDataCountRequest, filterParams);
        const data = yield call(getMetricData, filterParams,  undefined,  count);

        yield delay(200);
        yield put(getMetricDataByTaskSuccess(data));
    } catch (error) {
        yield put(getMetricDataByTaskError());
        getErrorMessage(error);
    }
}

function* handleGetMetricTaskByCode ({ payload }) {
    try {
        const data = yield call(getMetricTaskByCodeRequest, payload);

        yield put(updateMetric({
            lastStartAt: data.last_start_at,
            groupCode: data.metric_group_code,
            colors: data.colors,
            name: data.name
        }));
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleCheckUpdate (pid) {
    try {
        const { isOpen } = yield select(metricUpdatingDialogSelector);
        const value = yield call(pidProcessRequest, pid);

        const status = Number((value * 100).toFixed());
        yield put(mergeUpdateMetricTaskDialog({ status }));

        if(value !== 1 && isOpen){
            yield delay(500);
            yield handleCheckUpdate(pid);
        }else{
            yield put(setUpdateMetricTaskDialog());
            yield call(toast.success, "The Task has been successfully updated");
        }
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleUpdateMetricTaskInfo ({ payload }) {
    try {
        yield put(mergeUpdateMetricTaskDialog({ isLoading: true }));

        const { pid } = yield call(updateMetricTaskRequest, payload);

        yield handleCheckUpdate(pid);

        yield handleGetMetric();
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleRefreshMetricTaskInfo ({ payload }) {
    try {
        yield put(mergeUpdateMetricTaskDialog({ isLoading: true }));

        const { pid } = yield call(updateMetricTaskRequest, payload);

        yield handleCheckUpdate(pid);
        yield handleRefreshTask();
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleGetTaskByCode ({ payload }) {
    try {
        const data = yield call(getTaskByCodeRequest, payload);

        yield put(getTaskByCodeSuccess(data));
    } catch (error) {
        yield put(getTaskByCodeError());
        getErrorMessage(error);
    }
}

function* handleCreateTask ({ payload }) {
    try {
        yield call(createTaskRequest, payload);
        yield call(toast.success, "The Task has been successfully created");

        sharedHistory.push(`${ROUTES_MAP.metric.path}/tasks`);
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleUpdateTask ({ payload }) {
    try {
        yield call(updateTaskRequest, payload);

        yield call(toast.success, `The Task - ${payload.code} has been successfully updated`);

        yield handleGetMetric();
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleFormUpdateMetricTask ({ payload }) {
    try {
        yield call(updateAllTaskDataRequest, payload);

        yield call(toast.success, `The Task - ${payload.code} has been successfully updated`);

        sharedHistory.push(`${ROUTES_MAP.metric.path}/tasks`);
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleDeleteMetricTask ({ payload }) {
    try {
        yield call(deleteTaskRequest, payload);

        yield call(toast.success, `The Task - ${payload} has been successfully deleted`);

        yield handleGetMetric();
    } catch (error) {
        getErrorMessage(error);
    }
}

// Watchers
export function* metricDashboardSagas() {
    yield all([
        yield takeLatest(getMetricTasks, handleGetMetric),
        yield takeLatest(getMetricDataByTask, handleGetMetricDataByTask),
        yield takeLatest(getMetricTaskByCode, handleGetMetricTaskByCode),
        yield takeLatest(updateMetricTaskInfo, handleUpdateMetricTaskInfo),
        yield takeLatest(refreshMetricTaskInfo, handleRefreshMetricTaskInfo),
        yield takeLatest(getTaskByCode, handleGetTaskByCode),
        yield takeLatest(createMetricTask, handleCreateTask),
        yield takeLatest(updateMetricTask, handleUpdateTask),
        yield takeLatest(formUpdateMetricTask, handleFormUpdateMetricTask),
        yield takeLatest(deleteMetricTask, handleDeleteMetricTask)
    ]);
}
